import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useGiftCodesStats from "@babackapp/private-api-client/client/use-gift-codes-stats";
import useTranslation from "@babackapp/translations/use-translation";
import NumberFlow from "@number-flow/react";

function StatCard({
  title,
  value,
  isLoading,
  suffix,
}: {
  title: string;
  value: number;
  isLoading?: boolean;
  suffix?: string;
}) {
  return (
    <div className="border rounded-xl border-border bg-white shadow-sm p-4 min-h-14 flex flex-col gap-2 flex-1">
      <span className="font-medium text-xs text-muted-foreground">{title}</span>
      <span className="font-semibold text-xl">
        {isLoading ? (
          <span className="animate-pulse bg-gray-200 rounded w-16 h-6 inline-block" />
        ) : (
          <>
            <NumberFlow
              value={value}
              format={suffix ? { maximumFractionDigits: 2 } : undefined}
            />
            {suffix}
          </>
        )}
      </span>
    </div>
  );
}

type GiftCodesStatsHeaderProps = {
  fromTimestamp: number;
  toTimestamp: number;
};

export function GiftCodesStatsHeader({
  fromTimestamp,
  toTimestamp,
}: GiftCodesStatsHeaderProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const { data: stats, isLoading } = useGiftCodesStats({
    shopId: shop.id,
    fromTimestamp,
    toTimestamp,
  });

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <StatCard
        title={t("giftCodes.stats.ordersCount")}
        value={stats?.ordersCount ?? 0}
        isLoading={isLoading}
      />
      <StatCard
        title={t("giftCodes.stats.totalCount")}
        value={stats?.totalCount ?? 0}
        isLoading={isLoading}
      />
      <StatCard
        title={t("giftCodes.stats.usedCount")}
        value={stats?.usedCount ?? 0}
        isLoading={isLoading}
      />
      <StatCard
        title={t("giftCodes.stats.unusedCount")}
        value={stats?.unusedCount ?? 0}
        isLoading={isLoading}
      />
      <StatCard
        title={t("giftCodes.stats.usageRate")}
        value={stats?.usageRate ?? 0}
        isLoading={isLoading}
        suffix="%"
      />
    </div>
  );
}
